import Axios from "../config/axios-config";
import { saveAs } from "file-saver";
import fileName from "../utils/fileName";
import { showToast } from "../utils/showToast";

// ─────────────────────────────────────────────────────────────────────────────
// Picking & packing (PDF de armado del pedido).
//
// Cada vez que un comercio descarga etiquetas en /home, además se genera este
// PDF con el detalle de productos del MISMO lote de paquetes que salió en las
// etiquetas. El backend deriva todos los metadatos del lote desde el JWT del
// usuario; el body solo aporta los paquetes (con fallbacks de nombre).
// El flujo de etiquetas NUNCA se ve afectado por fallos de este módulo: los
// callers contienen el error y estas funciones no propagan excepciones que
// puedan romper la descarga de etiquetas.
// ─────────────────────────────────────────────────────────────────────────────

// true si ALGÚN paquete del lote trae detalle de productos (products[] o
// items[]). Si ninguno lo trae, no tiene sentido llamar al endpoint.
export const hasPickingProducts = (packages) => {
  if (!Array.isArray(packages)) {
    return false;
  }
  return packages.some(
    (pkg) => (pkg?.products?.length || pkg?.items?.length || 0) > 0
  );
};

// Medida utilizable: número finito y > 0. Un string, NaN, 0 o un negativo no es
// una medida: se omite en vez de mandarse, para que el PDF no imprima "0 cm" ni
// un valor que el backend no sabría interpretar. No se coaccionan strings
// numéricos: products[] ya se persiste con números (@IsNumber en packages).
const toPositiveMeasure = (value) =>
  typeof value === "number" && Number.isFinite(value) && value > 0
    ? value
    : undefined;

// Medidas por unidad en cm, forma canónica de products[] en microservice-packages
// ({ length, width, height }). Solo se envían los ejes válidos; si no queda
// ninguno, undefined. El guard de objeto es necesario: sobre un string o un
// array, `.length` devuelve su largo y pasaría por una medida real.
const pickProductDimensions = (dimensions) => {
  if (!dimensions || typeof dimensions !== "object" || Array.isArray(dimensions)) {
    return undefined;
  }
  const picked = {};
  for (const axis of ["length", "width", "height"]) {
    const measure = toPositiveMeasure(dimensions[axis]);
    if (measure !== undefined) {
      picked[axis] = measure;
    }
  }
  return Object.keys(picked).length ? picked : undefined;
};

// Comercio dueño del paquete, como id en texto, o null. En packages vive en
// `senderInfo.companyId` (id o comercio poblado { _id }), pero viaja en el
// `companyId` de primer nivel porque es ahí donde lo lee reports: con él cuenta
// los comercios distintos de un lote de operador ("Varios comercios") cuando a
// un paquete le falta el businessName.
const pickCompanyId = (senderInfo) => {
  const raw = senderInfo?.companyId;
  const id = raw !== null && typeof raw === "object" ? raw._id : raw;
  return typeof id === "string" && id.trim() !== "" ? id.trim() : null;
};

// Mapper whitelist por paquete, con EXACTAMENTE los campos del contrato del
// endpoint. Defensivo ante variaciones de forma de los datos de origen
// (products ?? items; name ?? productName; unitValue ?? price). NO filtra por
// teléfono: el lote lo decide el caller. SÍ descarta paquetes sin correlativo:
// el backend lo exige (@IsDefined) y sin él el documento no puede referenciar
// la etiqueta; un solo null invalidaría el lote completo con 400.
// `dimensions` (cm) y `volume` (cm³), ambos POR UNIDAD, viajan solo si traen
// medidas válidas: a diferencia del resto, no se mandan en null cuando faltan.
export const formatPackagesPickingPacking = (packages) => {
  if (!Array.isArray(packages)) {
    return [];
  }

  const withFollowingNumber = packages.filter(
    (pkg) =>
      pkg?.followingNumber !== null &&
      pkg?.followingNumber !== undefined &&
      String(pkg.followingNumber).trim() !== ""
  );

  return withFollowingNumber.map((pkg) => {
    const rawProducts = pkg?.products ?? pkg?.items ?? [];
    const products = Array.isArray(rawProducts)
      ? rawProducts.map((product) => {
          const dimensions = pickProductDimensions(product?.dimensions);
          const volume = toPositiveMeasure(product?.volume);
          return {
            name: product?.name ?? product?.productName ?? "",
            sku: product?.sku ?? null,
            quantity: product?.quantity ?? null,
            unitValue: product?.unitValue ?? product?.price ?? null,
            totalValue: product?.totalValue ?? null,
            weight: product?.weight ?? null,
            currency: product?.currency ?? null,
            ...(dimensions && { dimensions }),
            ...(volume !== undefined && { volume }),
          };
        })
      : [];

    return {
      _id: pkg?._id,
      followingNumber: pkg.followingNumber,
      order_id: pkg?.order_id ?? null,
      companyId: pickCompanyId(pkg?.senderInfo),
      type: pkg?.type ?? null,
      receiver_name: pkg?.receiver_name ?? "",
      receiver_lastName: pkg?.receiver_lastName ?? "",
      receiver_phone: pkg?.receiver_phone ?? null,
      // null se preserva (no se coacciona a ""): el backend cae al comment de
      // la dirección cuando comments no trae texto.
      comments: pkg?.comments ?? null,
      additionalLabels: pkg?.additionalLabels ?? null,
      declaredValue: pkg?.declaredValue ?? null,
      shipping_address: {
        street_name: pkg?.shipping_address?.street_name ?? "",
        street_number: pkg?.shipping_address?.street_number ?? "",
        address_line: pkg?.shipping_address?.address_line ?? "",
        comment: pkg?.shipping_address?.comment ?? "",
        municipality: { name: pkg?.shipping_address?.municipality?.name ?? "" },
        city: { name: pkg?.shipping_address?.city?.name ?? "" },
      },
      senderInfo: {
        businessName: pkg?.senderInfo?.businessName ?? "",
      },
      deliveryInfo: {
        deliveryBusinessName: pkg?.deliveryInfo?.deliveryBusinessName ?? "",
      },
      products,
    };
  });
};

// POST al endpoint del reporte. Devuelve { data } (p. ej. { url, ok }) en éxito
// o { error } en fallo, siguiendo el patrón de manejo de errores de downloadPdf.
// `extraBody` suma campos opcionales del contrato (hoy, `container`).
const requestPickingPackingPdf = async (packages, extraBody) => {
  try {
    const formattedPackages = formatPackagesPickingPacking(packages);
    if (!formattedPackages.length) {
      // Todos los paquetes quedaron descartados (sin correlativo): el backend
      // rechazaría el lote vacío con 400; se corta aquí sin llamar.
      return { error: { message: "No hay paquetes válidos para el picking & packing." } };
    }
    const response = await Axios.post("reports/picking-packing-pdf", {
      packages: formattedPackages,
      ...extraBody,
    });
    return { data: response.data };
  } catch (err) {
    console.error(
      "Error downloading picking & packing PDF:",
      err.response?.data || "Unknown error"
    );
    return { error: err.response?.data || "Unknown error" };
  }
};

export const downloadPickingPackingPdf = async (packages) =>
  requestPickingPackingPdf(packages, {});

// Límites de `container` en el DTO de reports (name 1..60, shortCode 1..20, con
// algún carácter visible). Se validan acá para no gastar el viaje en un 400.
const CONTAINER_NAME_MAX = 60;
const CONTAINER_SHORT_CODE_MAX = 20;

const pickContainerLabel = (container) => {
  const name = typeof container?.name === "string" ? container.name.trim() : "";
  const shortCode = typeof container?.shortCode === "string" ? container.shortCode.trim() : "";
  if (!name || name.length > CONTAINER_NAME_MAX) return null;
  if (!shortCode || shortCode.length > CONTAINER_SHORT_CODE_MAX) return null;
  return { name, shortCode };
};

// Picking & packing de una cesta: el mismo PDF, con "Cesta: <name> · <shortCode>"
// en el encabezado de ambas hojas. `packages` son los docs crudos de los pedidos
// de la cesta (ver docsDeCesta en el Rutificador). Misma respuesta que
// downloadPickingPackingPdf; sin nombre o código válidos no se llama al backend.
export const downloadContainerPickingPackingPdf = async (packages, container) => {
  const label = pickContainerLabel(container);
  if (!label) {
    return { error: { message: "La cesta no tiene nombre o código válidos para el picking & packing." } };
  }
  return requestPickingPackingPdf(packages, { container: label });
};

// Descarga el PDF desde la URL de S3. Preferimos fetch → blob → saveAs para
// forzar la descarga con nombre propio; si el fetch falla (p. ej. CORS),
// caemos a abrir la URL en una pestaña nueva. Devuelve true si el archivo
// llegó al usuario (descargado o abierto) y false si tampoco se pudo abrir
// (p. ej. pop-up bloqueado fuera del gesto de usuario): el caller no debe
// anunciar éxito en ese caso.
export const savePickingPackingFromUrl = async (url) => {
  try {
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }
    const blob = await response.blob();
    saveAs(blob, fileName("pdf", "PickingPackingOTS"));
    return true;
  } catch (err) {
    console.warn(
      "No se pudo descargar el picking & packing vía fetch, se abre en pestaña nueva:",
      err
    );
    const opened = window.open(url);
    return Boolean(opened);
  }
};

// Orquestación compartida (hoy la usa downloadLabelsModal) para
// no duplicar el flujo: sin productos → skip + toast; éxito → descarga + toast;
// fallo → toast warning. Contenida: nunca propaga excepciones, para no romper
// jamás el flujo de etiquetas del caller.
export const generatePickingPackingForBatch = async ({ packages, t }) => {
  try {
    if (!hasPickingProducts(packages)) {
      showToast(
        t("El lote no incluye detalle de productos; no se generó el picking & packing."),
        "warning"
      );
      return;
    }

    const { data, error } = await downloadPickingPackingPdf(packages);

    if (error || !data?.url) {
      showToast(t("Las etiquetas se descargaron, pero falló el picking & packing."), "warning");
      return;
    }

    const delivered = await savePickingPackingFromUrl(data.url);
    if (!delivered) {
      // El PDF existe pero no llegó al usuario (fetch falló y el pop-up fue
      // bloqueado): no anunciar un éxito falso.
      showToast(t("Las etiquetas se descargaron, pero falló el picking & packing."), "warning");
      return;
    }
    showToast(t("Picking & packing descargado."), true);
  } catch (err) {
    console.error("Error en generatePickingPackingForBatch:", err);
    showToast(t("Las etiquetas se descargaron, pero falló el picking & packing."), "warning");
  }
};
